import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { BookOpen, Heart, Crown, ChevronRight } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    {
      icon: BookOpen,
      title: 'Centenas de Contos',
      description: 'Biblioteca vasta com contos de diversos géneros e fantasias'
    },
    {
      icon: Heart,
      title: 'Favoritos & Histórico',
      description: 'Guarda os teus contos preferidos e retoma de onde paraste'
    },
    {
      icon: Crown,
      title: 'Conteúdo Exclusivo',
      description: 'Acesso premium a contos especiais de autores renomados'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ruby/20 border border-ruby/30 mb-6 animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-ruby animate-pulse" />
              <span className="text-sm text-ruby-light">+18 Conteúdo Adulto</span>
            </div>

            {/* Title */}
            <h1 className="font-brand text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 animate-slide-up leading-tight">
              A tua biblioteca de <span className="text-ruby">desejos</span>
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-8 animate-slide-up stagger-1 font-reading">
              Descobre centenas de contos eróticos cuidadosamente selecionados.
              Explora as tuas fantasias num ambiente seguro e discreto.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up stagger-2">
              {user ? (
                <Button
                  onClick={() => navigate('/home')}
                  data-testid="landing-explora-btn"
                  className="bg-ruby hover:bg-ruby-light text-white rounded-full px-8 py-6 text-lg font-semibold animate-pulse-glow"
                >
                  Explorar Contos
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => navigate('/register')}
                    data-testid="landing-register-btn"
                    className="bg-ruby hover:bg-ruby-light text-white rounded-full px-8 py-6 text-lg font-semibold animate-pulse-glow"
                  >
                    Começar Grátis
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/home')}
                    data-testid="landing-browse-btn"
                    className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg backdrop-blur-sm"
                  >
                    Explorar como Visitante
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-surface">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-brand text-3xl md:text-4xl font-semibold text-white text-center mb-16">
            Por que escolher o <span className="text-ruby">Intimate</span>.pt?
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="group p-8 rounded-xl bg-surface-light border border-border hover:border-ruby/30 transition-all duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-xl bg-ruby/10 flex items-center justify-center mb-6 group-hover:bg-ruby/20 transition-colors">
                  <feature.icon className="w-7 h-7 text-ruby" />
                </div>
                <h3 className="font-brand text-xl font-semibold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-brand text-3xl md:text-4xl font-semibold text-white mb-6">
            Pronto para explorares as tuas <span className="text-ruby">fantasias</span>?
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            Junta-te a milhares de leitores que já descobriram o prazer da leitura erótica.
          </p>
          {!user && (
            <Button
              onClick={() => navigate('/register')}
              data-testid="landing-cta-btn"
              className="bg-ruby hover:bg-ruby-light text-white rounded-full px-12 py-6 text-lg font-semibold animate-pulse-glow"
            >
              Criar Conta Grátis
            </Button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo-white.png" alt="Intimate.pt" className="h-8 w-auto" />
          <p className="text-sm text-muted-foreground">
            © 2026 Intimate.pt. Conteúdo exclusivo para maiores de 18 anos.
          </p>
        </div>
      </footer>
    </div>
  );
}
